import type * as d3 from 'd3';

import type {
  CodingResultsSubtopic,
  CodingResultSubTopicTotal,
} from 'src/types/dashboard';
import {
  ARC_THICKNESS,
  CLASS_NAME,
  CLASS_NAME_TARGET,
  LINE_HEIGHT,
  MARKER_DISTANCE_FROM_WHEEL,
  WHEEL_HEIGHT,
  WHEEL_RADIUS,
  WHEEL_WIDTH,
} from 'src/components/dashboard/DashboardPage/widgets/InsightWheelWidget/InsightWheel/constants';

export type LabelPositions = [number, number][];

export const linkageMatrix = (
  subTopics: CodingResultsSubtopic[] | CodingResultSubTopicTotal[],
): number[][] => {
  const matrix: number[][] = subTopics.map(() =>
    new Array(subTopics.length).fill(0),
  );
  subTopics.forEach((subTopic, subTopicIndex) => {
    const row = matrix[subTopicIndex];
    if (!row) return [];

    const subTopicEntries = Object.entries(subTopic.linkage || {});
    const linkageTotal = subTopicEntries.reduce((acc, [targetId, value]) => {
      const targetIndex = subTopics.findIndex(
        ({ id }) => String(targetId) === String(id),
      );
      if (targetIndex >= 0) {
        return acc + value;
      }
      return acc;
    }, 0);
    subTopicEntries.forEach(([targetId, value], _) => {
      const targetIndex = subTopics.findIndex(
        ({ id }) => String(targetId) === String(id),
      );
      if (targetIndex >= 0) {
        // normalize the value and factor in the subtopic count
        row[targetIndex] = value * subTopic.count * (1 / linkageTotal);
      }
    });
  });
  return matrix;
};

// calculate the space needed above and below the diagram to fit all labels
export const getChartDimensions = (
  labelPositions: [number, number][],
): [number, number] => {
  let maxHeight = 0;
  let maxWidth = 0;
  labelPositions.forEach(([x, y]) => {
    maxHeight = Math.max(maxHeight, Math.abs(y));
    maxWidth = Math.max(maxWidth, Math.abs(x));
  });
  const chordWidth = Math.max(2 * maxWidth, WHEEL_WIDTH);
  const chordHeight = Math.max(2 * (maxHeight + LINE_HEIGHT), WHEEL_HEIGHT);
  return [chordWidth, chordHeight];
};

export const arcRadiusDifference = (
  startAngle: number,
  endAngle: number,
): number => {
  const weight = endAngle - startAngle;
  return (ARC_THICKNESS * (1 + (weight * 4) / 2)) / 4;
};

export const getLabelPositions = (
  chordGroups: d3.ChordGroup[],
): LabelPositions =>
  chordGroups.map((chordGroup) => {
    const r = WHEEL_RADIUS + MARKER_DISTANCE_FROM_WHEEL;
    const angle =
      (chordGroup.startAngle + chordGroup.endAngle) / 2 - Math.PI / 2;
    const x = Math.cos(angle) * r;
    const y = Math.sin(angle) * r;
    return [x, y > 0 ? y + 10 : y - 10];
  });

export const shiftLabelsEvenQuadrants = (labelPositions: LabelPositions) => {
  // shift labels in upper left and lower right quadrant to avoid overlap in y direction
  for (let i = 1; i < labelPositions.length; i++) {
    const a = labelPositions[i];
    const b = labelPositions[i - 1];
    if (a[0] < 0 && a[1] < 0) {
      const overlap = Math.max(a[1] - b[1] + LINE_HEIGHT + 5, 0);
      a[1] -= overlap;
    } else if (a[0] > 0 && a[1] > 0) {
      const overlap = Math.max(b[1] - a[1] + LINE_HEIGHT + 5, 0);
      a[1] += overlap;
    }
  }
};

export const shiftLabelsOddQuadrants = (labelPositions: LabelPositions) => {
  // shift labels in lower left and upper right quadrant
  for (let i = labelPositions.length - 2; i >= 0; i--) {
    const a = labelPositions[i];
    const b = labelPositions[i + 1];
    if (a[0] < 0 && a[1] > 0) {
      const overlap = Math.max(b[1] - a[1] + LINE_HEIGHT + 5, 0);
      a[1] += overlap;
    } else if (a[0] > 0 && a[1] < 0) {
      const overlap = Math.max(a[1] - b[1] + LINE_HEIGHT + 5, 0);
      a[1] -= overlap;
    }
  }
};

export const d3SelectionAppendClassTo = (
  selection: d3.Selection<any, any, any, any> | d3.TransitionLike<any, any>,
  index: number,
  className: string,
  groupIndex = 0,
) => {
  // @ts-expect-error: Private property does not exist
  const groups: SVGGElement[][] = selection._groups;
  const targetLabels = groups?.[groupIndex]?.[index];
  if (targetLabels && targetLabels.classList) {
    targetLabels.classList.add(className);
  }
};

const mouseEnterEvent = (elGroups: NodeListOf<Element>[], index: number) => {
  elGroups.forEach((group) => {
    group.forEach((el) => {
      const element = el as HTMLElement;
      if (
        element.classList &&
        element.style &&
        // some class lists do not have 'contains' available so an array is used instead
        ![...element.classList].includes(`${CLASS_NAME_TARGET}-${index}`)
      ) {
        // reduce opacity of elements that are not the current target element
        element.style.opacity = '0.2';
      }
    });
  });
};

const mouseLeaveEvent = (elGroups: NodeListOf<Element>[]) => {
  elGroups.forEach((group) => {
    group.forEach((el) => {
      const element = el as HTMLElement;
      if (element.style) element.style.opacity = '1';
    });
  });
};

type EventListener = [Element, () => void, () => void][];

export const addEventListener = (
  container: HTMLElement,
  subTopics: CodingResultsSubtopic[] | CodingResultSubTopicTotal[],
) => {
  const dataGroups = subTopics.map((_, index) =>
    container.querySelectorAll(`.${CLASS_NAME}-${index}`),
  );
  const eventListener: EventListener = [];
  dataGroups.forEach((elGroup, index) => {
    // filter all other elements in order to manipulate them when interacting with the current element
    const otherElGroups = dataGroups.filter((group) => group !== elGroup);
    // add events to the element and return them to be able to remove them later
    elGroup.forEach((el) => {
      const enterEvent = () => mouseEnterEvent(otherElGroups, index);
      el.addEventListener('mouseenter', enterEvent);
      const leaveEvent = () => mouseLeaveEvent(otherElGroups);
      el.addEventListener('mouseleave', leaveEvent);
      eventListener.push([el, enterEvent, leaveEvent]);
    });
  });
  return eventListener;
};

export const removeEventListener = (eventListener: EventListener) => {
  eventListener.forEach(([el, enterEvent, leaveEvent]) => {
    el.removeEventListener('mouseenter', enterEvent);
    el.removeEventListener('mouseleave', leaveEvent);
  });
};
